import { Platform, StyleSheet } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'
import type { ThemeMode } from '../../../src/theme/ThemeContext'

/**
 * `compact` — right-aligns (e.g. scoreboard).
 * `sportsBar` — neutral grey track, white floating thumb (reference / league table); `themeMode` picks light vs dark neutrals.
 */
export type AnimatedSegmentedControlVariant = 'default' | 'compact' | 'sportsBar'

const BORDER_WIDTH = StyleSheet.hairlineWidth

export const getAnimatedSegmentedControlStyles = (
  theme: Theme,
  variant: AnimatedSegmentedControlVariant,
  themeMode: ThemeMode = 'light',
  segmentWidth = 0,
  fullWidth = false,
) => {
  const compact = variant === 'compact'
  const sportsBar = variant === 'sportsBar'

  const labelFs = 13
  const labelLineHeight = 18
  const outerPadH = 3
  const outerPadV = 3
  const segmentPadV = 7
  const segmentPadH = compact ? 12 : 16

  if (sportsBar) {
    /** Physical neutrals so inverted `theme.gray` in dark mode doesn't flip the track to white. */
    const trackBg = themeMode === 'dark' ? '#3F3F46' : '#E5E7EB'
    const labelInactive = themeMode === 'dark' ? '#A1A1AA' : '#4B5563'
    return StyleSheet.create({
      outer: {
        alignSelf: fullWidth ? 'stretch' : 'flex-start',
        flexDirection: 'row',
        borderRadius: 999,
        backgroundColor: trackBg,
        borderWidth: 0,
        paddingHorizontal: outerPadH,
        paddingVertical: outerPadV,
      },
      track: {
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'stretch',
        ...(fullWidth ? { width: '100%' } : {}),
      },
      thumb: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: segmentWidth > 0 ? segmentWidth : 0,
        backgroundColor: theme.white,
        borderRadius: 999,
        zIndex: 0,
        ...(Platform.OS === 'web'
          ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)' }
          : {
              shadowColor: theme.black,
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.12,
              shadowRadius: 3,
              elevation: 2,
            }),
      },
      segmentPressable: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: segmentPadV,
        paddingHorizontal: compact ? 10 : 12,
        zIndex: 1,
        ...(fullWidth && segmentWidth <= 0 ? { flex: 1 } : {}),
        ...(segmentWidth > 0 ? { width: segmentWidth } : {}),
      },
      segmentPressed: {
        opacity: 0.88,
      },
      segmentLabel: {
        fontSize: labelFs,
        lineHeight: labelLineHeight,
        fontWeight: '600',
        letterSpacing: 0.2,
        color: labelInactive,
        textAlign: 'center',
        ...(Platform.OS === 'android'
          ? { includeFontPadding: false, textAlignVertical: 'center' as const }
          : {}),
      },
      segmentLabelActive: {
        fontWeight: '700',
        color: theme.textOnCard,
      },
    })
  }

  return StyleSheet.create({
    outer: {
      alignSelf: fullWidth ? 'stretch' : compact ? 'flex-end' : 'flex-start',
      flexDirection: 'row',
      borderRadius: 999,
      backgroundColor: theme.background.cardElevated,
      borderWidth: BORDER_WIDTH,
      borderColor: theme.gray[200],
      paddingHorizontal: outerPadH,
      paddingVertical: outerPadV,
      ...(Platform.OS === 'web'
        ? { boxShadow: theme.shadows.card }
        : {
            shadowColor: theme.black,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.08,
            shadowRadius: 3,
            elevation: 1,
          }),
    },
    track: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'stretch',
      ...(fullWidth ? { width: '100%' } : {}),
    },
    thumb: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: segmentWidth > 0 ? segmentWidth : 0,
      backgroundColor: theme.primary,
      borderRadius: 999,
      zIndex: 0,
      ...(Platform.OS === 'web'
        ? {
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.18)',
          }
        : {
            shadowColor: theme.black,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.18,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    segmentPressable: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 5,
      paddingVertical: segmentPadV,
      paddingHorizontal: segmentPadH,
      zIndex: 1,
      ...(fullWidth && segmentWidth <= 0 ? { flex: 1 } : {}),
      ...(segmentWidth > 0 ? { width: segmentWidth } : {}),
    },
    segmentPressed: {
      opacity: 0.85,
    },
    segmentLabel: {
      fontSize: labelFs,
      lineHeight: labelLineHeight,
      fontWeight: '600',
      letterSpacing: 0.2,
      color: theme.gray[600],
      textAlign: 'center',
      ...(Platform.OS === 'android'
        ? { includeFontPadding: false, textAlignVertical: 'center' as const }
        : {}),
    },
    segmentLabelActive: {
      fontWeight: '700',
      color: theme.onPrimary,
    },
  })
}
